import { Button, ButtonGroup, DeleteIcon, DropdownMenu, EditIcon, ImportIcon, Table, Tooltip } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useLessonUsage } from '../../../hooks/useLessonUsage';
import { UsedInLessonsTooltipContent } from '../../TooltipContents/LessonUsedInTooltipContent/UsedInLessonsTooltipContent';
import { useClassesTable } from '../hooks/useClassesTable';
import { ClassesTableType } from '../types/ClassesTypes';

type DeputateClassesTableProps = {
  loading: boolean;
  onAdd: () => void;
  onInherit: () => void;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string) => void;
  readonly: boolean;
};

export const DeputateClassesTable = observer(
  ({ loading, onAdd, onInherit, onEdit, onDelete, readonly }: DeputateClassesTableProps) => {
    const { t } = useTranslation();

    const {
      data,
      columns,
      tableSettings,
      tableSorting,
      tableVisibility,
      tableSelection,
      setRandomColors,
      emptyStateSettings,
      printerSettings,
      actionBarSettings,
    } = useClassesTable({
      context: 'deputate',
      columns: [
        'name',
        'shortName',
        'grade',
        'gradeGroup',
        'tutor1',
        'tutor2',
        'tutor3',
        'room',
        'studentCount',
        'printOrder',
        'printPageNr',
        'color',
      ],
    });

    const check = useLessonUsage('class', data);

    return (
      <Table<ClassesTableType>
        loading={loading}
        data={data}
        columns={columns}
        {...tableSettings}
        {...tableSorting}
        {...tableVisibility}
        {...tableSelection}
        printerSettings={printerSettings}
        actionBarSettings={{
          ...actionBarSettings,
          disableAddButton: readonly,
          showBulkEdit: true,
          disableBulkEdit: readonly,
          extendedActionsRight: (
            <Button hierarchy='tertiary' icon={<ImportIcon />} onClick={onInherit} disabled={readonly}>
              {t('common.inherit')}
            </Button>
          ),
        }}
        bulkEditDropdownContent={(rows) => (
          <DropdownMenu
            data={[
              {
                label: t('common.addRandomColor'),
                onClick: async () => {
                  await setRandomColors(rows.map(({ original }) => original.uuid));
                  tableSelection.clearSelection();
                },
              },
            ]}
          />
        )}
        emptyStateSettings={{ ...emptyStateSettings, showButton: !readonly, onButtonClick: onInherit }}
        onAddClick={onAdd}
        lastCol={(row) => {
          const used = check(row.original.uuid);
          const canBeDeleted = !readonly && !used.isUsed;
          const deleteButton = (
            <Button
              disabled={!canBeDeleted}
              hierarchy='tertiary'
              type='button'
              icon={<DeleteIcon className='small' color={canBeDeleted ? 'var(--color-error)' : undefined} />}
              onClick={() => onDelete(row.original.uuid)}
            />
          );
          return (
            <ButtonGroup disabled={readonly}>
              <Button
                hierarchy='tertiary'
                type='button'
                onClick={() => onEdit(row.original.uuid)}
                icon={<EditIcon className='small' />}
                disabled={readonly}
              />
              {used.isUsed ? (
                <Tooltip
                  content={
                    <UsedInLessonsTooltipContent
                      lessons={used.lessons}
                      lessonsCount={used.lessonsCount}
                      usedInText={t('classes.usedInLesson')}
                    />
                  }
                >
                  {deleteButton}
                </Tooltip>
              ) : (
                deleteButton
              )}
            </ButtonGroup>
          );
        }}
      />
    );
  },
);
